import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Edit, Save, X, Calendar, Clock, MapPin, Link as LinkIcon, Ticket, ShoppingCart, ExternalLink, QrCode, Users, Euro } from 'lucide-react';
import { toast } from "sonner";
import { ImageDropzone } from '@/components/ImageDropzone';
import { TicketManager } from './TicketManager';
import { OrderManager } from './OrderManager';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const EventsManager = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedEventForTickets, setSelectedEventForTickets] = useState(null);
  const [ticketView, setTicketView] = useState('tickets'); // 'tickets' or 'orders'
  const [listView, setListView] = useState('upcoming'); // 'upcoming' or 'past'

  const fetchEvents = async () => {
    try {
      // Use the new API that calculates ticket stats from orders (not sold_count!)
      const response = await axios.get(`${BACKEND_URL}/api/events/with-stats`);
      setEvents(response.data);
    } catch (error) {
      // Fallback to old API if new one not available
      try {
        const response = await axios.get(`${BACKEND_URL}/api/events`);
        setEvents(response.data);
      } catch (e) {
        toast.error("Fehler beim Laden der Events");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Dieses Event wirklich löschen?")) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/events/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Event gelöscht");
      fetchEvents();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    try {
      if (isNew) {
        await axios.post(`${BACKEND_URL}/api/events`, editingEvent, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Event erstellt");
      } else {
        await axios.put(`${BACKEND_URL}/api/events/${editingEvent.id}`, editingEvent, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Event aktualisiert");
      }
      setEditingEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const startNew = () => {
    setEditingEvent({
      title: '',
      date: new Date().toISOString().split('T')[0],
      time: '18:00',
      description: '',
      ticket_link: '',
      flyer_url: '',
      flyer_url_2: '',
      location: ''
    });
    setIsNew(true);
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('de-DE', { 
        weekday: 'short',
        day: '2-digit', 
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-black border-t-transparent rounded-full mx-auto"></div>
        <p className="mt-4 text-gray-500">Laden...</p>
      </div>
    );
  }

  // Tagesgenaue Trennung, gleiche Regel wie im oeffentlichen EventList:
  // am Veranstaltungstag zaehlt ein Event noch als kommend, ab dem Folgetag
  // ist es vergangen.
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const isPast = (event) => {
    const d = new Date(event.date);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()) < startOfToday;
  };
  const upcomingEvents = events
    .filter((e) => !isPast(e))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const pastEvents = events
    .filter(isPast)
    .sort((a, b) => new Date(b.date) - new Date(a.date)); // zuletzt gelaufenes oben
  const visibleEvents = listView === 'past' ? pastEvents : upcomingEvents;

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Veranstaltungen</h1>
          <p className="text-gray-500 mt-2">Events erstellen und verwalten</p>
        </div>
        <Button onClick={startNew} className="gap-2 bg-black hover:bg-gray-800">
          <Plus size={16} /> Neues Event
        </Button>
      </div>

      {/* Umschalter kommende / vergangene Events */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {[
          { key: 'upcoming', label: 'Kommende', count: upcomingEvents.length },
          { key: 'past', label: 'Vergangene', count: pastEvents.length },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setListView(tab.key)}
            className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 transition-colors ${
              listView === tab.key
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Events List */}
      <div className="grid gap-4">
        {visibleEvents.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
              {listView === 'past' ? 'Keine vergangenen Events' : 'Keine kommenden Events'}
            </h3>
            {listView === 'past' ? (
              <p className="text-gray-400 mb-4">
                Hier landen Events automatisch am Tag nach der Veranstaltung.
              </p>
            ) : (
              <>
                <p className="text-gray-400 mb-4">Erstellen Sie Ihr erstes Event</p>
                <Button onClick={startNew} variant="outline" className="gap-2">
                  <Plus size={16} /> Event erstellen
                </Button>
              </>
            )}
          </div>
        ) : (
          visibleEvents.map((event) => (
            <div 
              key={event.id} 
              className={`bg-white border rounded-xl p-4 flex gap-4 items-start group hover:shadow-md transition-shadow ${
                listView === 'past' ? 'border-gray-200 opacity-70 hover:opacity-100' : 'border-gray-200'
              }`}
            >
              {/* Flyer Preview */}
              {event.flyer_url && (
                <div className="w-24 h-32 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200">
                  <img 
                    src={event.flyer_url} 
                    alt={event.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              {/* Event Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-gray-900 truncate">{event.title}</h3>
                      {listView === 'past' && (
                        <span className="shrink-0 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">
                          vergangen
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {formatDate(event.date)}
                      </span>
                      {event.location && (
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {event.location}
                        </span>
                      )}
                    </div>
                    
                    {/* Ticket Statistics - Calculated from Orders (NOT sold_count!) */}
                    {event.has_tickets && event.ticket_stats ? (
                      <div className="flex items-center gap-4 mt-3 py-2 px-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                            <Ticket size={14} className="text-yellow-600" />
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">Verkauft</div>
                            <div className="font-bold text-green-600">{event.ticket_stats.sold}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <Users size={14} className="text-blue-600" />
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">Verfügbar</div>
                            <div className="font-bold text-blue-600">{event.ticket_stats.available ?? '∞'}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <Euro size={14} className="text-green-600" />
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">Umsatz</div>
                            <div className="font-bold text-green-600">{event.ticket_stats.revenue?.toFixed(2)} €</div>
                          </div>
                        </div>
                      </div>
                    ) : event.ticket_link ? (
                      <div className="mt-3 py-2 px-3 bg-gray-50 rounded-lg text-xs text-gray-500">
                        Externes Ticketsystem
                      </div>
                    ) : null}
                    
                    {event.description && (
                      <p className="text-gray-600 text-sm mt-2 line-clamp-2">{event.description}</p>
                    )}
                    {event.ticket_link && (
                      <a 
                        href={event.ticket_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-2"
                      >
                        <LinkIcon size={12} /> Externer Ticket-Link
                      </a>
                    )}
                    
                    {/* Ticket Shop Link */}
                    <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setSelectedEventForTickets(event); setTicketView('tickets'); }}
                        className="text-xs"
                      >
                        <Ticket size={14} className="mr-1" />
                        Tickets verwalten
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setSelectedEventForTickets(event); setTicketView('orders'); }}
                        className="text-xs"
                      >
                        <ShoppingCart size={14} className="mr-1" />
                        Bestellungen
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => navigate(`/admin/checkin/${event.id}`)}
                        className="text-xs bg-green-600 hover:bg-green-700 text-white"
                      >
                        <QrCode size={14} className="mr-1" />
                        Check-in
                      </Button>
                      <a 
                        href={`/tickets/${event.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex"
                      >
                        <Button variant="outline" size="sm" className="text-xs">
                          <ExternalLink size={14} className="mr-1" />
                          Shop öffnen
                        </Button>
                      </a>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => { setEditingEvent(event); setIsNew(false); }}
                    >
                      <Edit size={18} className="text-gray-600" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDelete(event.id)}
                    >
                      <Trash2 size={18} className="text-red-500" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Edit Modal */}
      {editingEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold">{isNew ? 'Neues Event' : 'Event bearbeiten'}</h2>
              <button 
                onClick={() => setEditingEvent(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSave} className="p-6 space-y-6">
              {/* Flyer Upload with Drag & Drop */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Flyer / Event-Bild
                </label>
                <ImageDropzone
                  value={editingEvent.flyer_url}
                  onChange={(url) => setEditingEvent({...editingEvent, flyer_url: url})}
                  placeholder="Flyer hierher ziehen oder klicken"
                  previewHeight="h-64"
                />
              </div>

              {/* Flyer Seite 2 (optional) — zweiseitiger Flyer; leer = einseitig */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Flyer Seite 2 (optional)
                </label>
                <ImageDropzone
                  value={editingEvent.flyer_url_2}
                  onChange={(url) => setEditingEvent({...editingEvent, flyer_url_2: url})}
                  placeholder="Zweite Flyer-Seite hierher ziehen oder klicken"
                  previewHeight="h-64"
                />
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titel *
                </label>
                <Input 
                  required 
                  value={editingEvent.title} 
                  onChange={e => setEditingEvent({...editingEvent, title: e.target.value})}
                  placeholder="z.B. Hopfen & Herz - Craftbeer Tasting"
                />
              </div>

              {/* Date, Time & Location */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Datum *
                  </label>
                  <Input
                    required
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    value={(editingEvent.date || '').split('T')[0]}
                    onChange={e => {
                      const value = e.target.value; // 'YYYY-MM-DD', lokal, ohne Zeitzone
                      if (!value) {
                        setEditingEvent({...editingEvent, date: ''});
                        return;
                      }

                      // Reiner String-Vergleich - kein Date-Objekt, keine Zeitzonen-Falle
                      const todayStr = new Date().toISOString().split('T')[0];
                      if (value < todayStr) {
                        toast.error('Das Datum darf nicht in der Vergangenheit liegen');
                        return;
                      }

                      // Jahr plausibel? (aktuelles Jahr bis +10)
                      const currentYear = new Date().getFullYear();
                      const year = parseInt(value.slice(0, 4), 10);
                      if (year < currentYear || year > currentYear + 10) {
                        toast.error(`Das Jahr muss zwischen ${currentYear} und ${currentYear + 10} liegen`);
                        return;
                      }

                      setEditingEvent({...editingEvent, date: value});
                    }}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Events können nur für zukünftige Termine erstellt werden
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Uhrzeit (Einlass)
                  </label>
                  <Input
                    type="time"
                    value={editingEvent.time || ''}
                    onChange={e => setEditingEvent({...editingEvent, time: e.target.value})}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Erscheint auf Ticket &amp; Bestätigungsmail (Standard 18:00)
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ort
                  </label>
                  <Input 
                    value={editingEvent.location || ''} 
                    onChange={e => setEditingEvent({...editingEvent, location: e.target.value})}
                    placeholder="z.B. Pedro's Café & Bar"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Beschreibung *
                </label>
                <textarea 
                  required 
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent min-h-[100px] resize-none" 
                  value={editingEvent.description} 
                  onChange={e => setEditingEvent({...editingEvent, description: e.target.value})}
                  placeholder="Beschreiben Sie das Event..."
                />
              </div>

              {/* Ticket Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Externer Ticket-Link (optional)
                </label>
                <Input 
                  type="url"
                  value={editingEvent.ticket_link || ''} 
                  onChange={e => setEditingEvent({...editingEvent, ticket_link: e.target.value})}
                  placeholder="https://www.eventbrite.de/e/..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Für externe Veranstalter: Link zu Eventbrite, Ticketmaster oder anderem Ticket-System. 
                  Wenn gesetzt, werden KEINE internen Tickets verwendet.
                </p>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setEditingEvent(null)}
                >
                  Abbrechen
                </Button>
                <Button type="submit" className="bg-black hover:bg-gray-800">
                  {isNew ? 'Event erstellen' : 'Speichern'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ticket Management Modal */}
      {selectedEventForTickets && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl w-full max-w-4xl shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold">{selectedEventForTickets.title}</h2>
              <button 
                onClick={() => setSelectedEventForTickets(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div className="p-6">
              <Tabs value={ticketView} onValueChange={setTicketView}>
                <TabsList className="mb-6">
                  <TabsTrigger value="tickets" className="flex items-center gap-2">
                    <Ticket size={16} />
                    Ticket-Typen
                  </TabsTrigger>
                  <TabsTrigger value="orders" className="flex items-center gap-2">
                    <ShoppingCart size={16} />
                    Bestellungen
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="tickets">
                  <TicketManager 
                    eventId={selectedEventForTickets.id} 
                    eventTitle={selectedEventForTickets.title}
                  />
                </TabsContent>
                
                <TabsContent value="orders">
                  <OrderManager 
                    eventId={selectedEventForTickets.id}
                    eventTitle={selectedEventForTickets.title}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsManager;
